import React, { useEffect, useMemo, useState } from "react";
import { Button, Form, Spinner, Modal } from "react-bootstrap";
import { FaUserCircle, FaLock } from "react-icons/fa";
import api from "../../api";
import { toast } from "react-toastify";
import "./AdminUnitPage.css";
import "./AccessManagementUsersPage.css";
import "./permissions/permissions.css";
import { usePermissions } from "../../context/PermissionContext";
import PermissionLegend from "./permissions/PermissionLegend";
import PermissionSummaryHeader from "./permissions/PermissionSummaryHeader";
import PermissionFilterBar from "./permissions/PermissionFilterBar";
import PermissionModuleCard from "./permissions/PermissionModuleCard";
import PermissionUserDropdown from "./permissions/PermissionUserDropdown";
import {
  HIGH_RISK_MODULE_KEYS, currentState, SUPPORTED_SCOPES, scopeLabel,
} from "./permissions/permissionUtils";

// Same deterministic role -> accent color helper as AccessManagementRolesPage.jsx
// (duplicated rather than shared, per that page's own precedent -- these two
// Access Management pages are already independent siblings, each with their
// own CSS file/helpers). Purely presentational; never used for any
// access-control decision.
const ROLE_COLORS = [
  { bg: "#f3e8ff", fg: "#7c3aed" },
  { bg: "#e0f2fe", fg: "#0369a1" },
  { bg: "#fef3c7", fg: "#92400e" },
  { bg: "#dcfce7", fg: "#15803d" },
  { bg: "#fbeef1", fg: "#6d1530" },
  { bg: "#ffe4e6", fg: "#be123c" },
  { bg: "#fee2e2", fg: "#b91c1c" },
  { bg: "#ede9fe", fg: "#5b21b6" },
];
function colorForRole(roleName) {
  const key = String(roleName || "").trim().toLowerCase();
  if (!key) return ROLE_COLORS[4];
  let hash = 0;
  for (let i = 0; i < key.length; i++) hash = (hash * 31 + key.charCodeAt(i)) >>> 0;
  return ROLE_COLORS[hash % ROLE_COLORS.length];
}

// Server-side user-list filters -- restored exactly as this page originally
// supported them (search/role/department/designation/active, all sent to
// GET /admin/access-management/users). Only their UI moved, into
// PermissionUserDropdown.jsx's filter row -- the filter STATE and the
// fetchUsers(filters) call that applies it stay owned here, same as before.
const EMPTY_FILTERS = { search: "", roleFilter: "", departmentFilter: "", designationFilter: "", activeFilter: "" };

export default function AccessManagementUsersPage() {
  const [filters, setFilters] = useState(EMPTY_FILTERS);

  const [roleOptions, setRoleOptions] = useState([]);
  const [departmentOptions, setDepartmentOptions] = useState([]);
  const [designationOptions, setDesignationOptions] = useState([]);

  const [users, setUsers] = useState([]);
  const [orgColumnsAvailable, setOrgColumnsAvailable] = useState({ department: true, designation: true, employeeCode: true });
  const [loadingUsers, setLoadingUsers] = useState(false);
  const [userListError, setUserListError] = useState(null);

  const { can } = usePermissions();
  const canManage = can("access_management", "manage_users");

  const [selectedUserId, setSelectedUserId] = useState(null);
  const [userDetail, setUserDetail] = useState(null); // { user, isLegacyInactiveBypass, modules }
  const [loadingMatrix, setLoadingMatrix] = useState(false);
  const [matrixError, setMatrixError] = useState(null);

  // Which row (by "${moduleId}::${permissionId}") currently has its
  // Allow-scope / reason / validity popover open -- at most one at a time.
  // Only ALLOW (always needs a data scope) and DENY-on-a-high-risk-module
  // (always needs a reason) open this before saving; INHERIT and a plain
  // DENY commit immediately with no popover.
  const [openPopoverKey, setOpenPopoverKey] = useState(null);
  // The single in-progress draft for whichever row's popover is open --
  // cleared (never persisted) on Cancel or after a successful/failed save.
  const [draftChange, setDraftChange] = useState(null); // { permissionId, state, dataScope, reason, validFrom, validUntil }

  // Per-row immediate-save UI feedback -- Saving.../Saved/error, keyed by
  // permissionId. Every override change (radio click or popover Apply)
  // saves immediately via the existing batch PUT endpoint called with a
  // single-item `changes` array; nothing is queued for a later "Save All".
  const [rowSaving, setRowSaving] = useState(() => new Set());
  const [rowJustSaved, setRowJustSaved] = useState(() => new Set());
  const [rowError, setRowError] = useState(() => new Map());

  // Module-level bulk action confirmation ("Allow All" / "Deny All") --
  // "Set All to Inherit" needs no confirmation since it only resets rows
  // back to their role-based default, it never grants or denies anything.
  const [bulkAction, setBulkAction] = useState(null); // { moduleId, moduleName, type: 'ALLOW_ALL'|'DENY_ALL', scope, reason, permissions }
  const [bulkSaving, setBulkSaving] = useState(false);

  // Which modules' permission tables are expanded -- same convention as
  // AccessManagementRolesPage.jsx's own module expand/collapse.
  const [expanded, setExpanded] = useState(() => new Set());

  // Page-level filters over the permission rows shown within expanded
  // modules (in addition to the existing per-module moduleSearch below).
  const [statusFilter, setStatusFilter] = useState("");
  const [moduleFilterId, setModuleFilterId] = useState("");

  function toggleExpand(moduleId) {
    setExpanded((prev) => {
      const next = new Set(prev);
      if (next.has(moduleId)) next.delete(moduleId); else next.add(moduleId);
      return next;
    });
  }

  // Lookup lists for the filter selects -- same endpoints already used by
  // DepartmentManagementPage.jsx / AdminStaffPage.js, reused as-is (no new
  // backend surface needed). IDs are still what's sent to the API; only the
  // display uses names.
  useEffect(() => {
    api.get("/roles").then((res) => setRoleOptions(Array.isArray(res.data) ? res.data : [])).catch(() => setRoleOptions([]));
    api.get("/departments", { params: { pageSize: 200 } })
      .then((res) => setDepartmentOptions(Array.isArray(res.data?.data) ? res.data.data : []))
      .catch(() => setDepartmentOptions([]));
    api.get("/designations", { params: { pageSize: 200 } })
      .then((res) => setDesignationOptions(Array.isArray(res.data?.data) ? res.data.data : []))
      .catch(() => setDesignationOptions([]));
  }, []);

  // Server-side user list fetch -- unchanged from the page's original
  // behavior: search/role/department/designation/active are all sent to
  // GET /admin/access-management/users, which does the actual filtering
  // (services/accessManagementService.js#listUsers). `activeFilters`
  // defaults to the current `filters` state but accepts an explicit
  // override so a caller can fetch with a value that hasn't been committed
  // to state yet (see applyFilterChange/clearFilters below).
  const fetchUsers = (activeFilters = filters) => {
    setLoadingUsers(true);
    setUserListError(null);
    api.get("/admin/access-management/users", {
      params: {
        search: activeFilters.search || undefined,
        role_id: activeFilters.roleFilter || undefined,
        department_id: activeFilters.departmentFilter || undefined,
        designation_id: activeFilters.designationFilter || undefined,
        is_active: activeFilters.activeFilter || undefined,
      },
    })
      .then((res) => {
        setUsers(res.data?.data || []);
        if (res.data?.meta?.organizationColumnsAvailable) setOrgColumnsAvailable(res.data.meta.organizationColumnsAvailable);
      })
      .catch((err) => setUserListError(err?.response?.data?.message || "Failed to load users."))
      .finally(() => setLoadingUsers(false));
  };

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { fetchUsers(EMPTY_FILTERS); }, []);

  // `search` is staged on every keystroke (see PermissionUserDropdown's
  // search input) but only actually queried on Enter -- same as the page's
  // original search box. Role/Department/Designation/Active instead
  // auto-apply the moment they change (applyFilterChange below): they're
  // single-value selects/chips inside a compact dropdown panel, not a
  // multi-field form with its own submit button, so applying immediately
  // reads more naturally there than requiring a separate "Apply" action.
  function updateFilter(key, value) {
    setFilters((prev) => ({ ...prev, [key]: value }));
  }

  // Computes the merged filters object explicitly and fetches with THAT
  // value, rather than calling fetchUsers() (which would default to the
  // `filters` state) -- setFilters() here hasn't been committed yet when
  // this runs, so relying on the `filters` closure would fetch with the
  // previous, stale value.
  function applyFilterChange(key, value) {
    const next = { ...filters, [key]: value };
    setFilters(next);
    fetchUsers(next);
  }

  function clearFilters() {
    setFilters(EMPTY_FILTERS);
    fetchUsers(EMPTY_FILTERS);
  }

  const loadMatrix = (userId) => {
    if (!userId) return;
    setLoadingMatrix(true);
    setMatrixError(null);
    setOpenPopoverKey(null);
    setDraftChange(null);
    api.get(`/admin/access-management/users/${userId}/permissions`)
      .then((res) => setUserDetail(res.data?.data || null))
      .catch((err) => {
        setMatrixError(err?.response?.data?.message || "Failed to load this user's permissions.");
        setUserDetail(null);
      })
      .finally(() => setLoadingMatrix(false));
  };

  // Silent refetch after a successful immediate save -- re-derives every
  // permission's effective access from the server (the single source of
  // truth) without blanking the panel behind a full-page spinner the way
  // loadMatrix does for the initial per-user load.
  const refreshMatrix = (userId) =>
    api.get(`/admin/access-management/users/${userId}/permissions`).then((res) => {
      setUserDetail(res.data?.data || null);
    });

  function selectUser(userId) {
    setSelectedUserId(userId);
    loadMatrix(userId);
  }

  function updateDraft(patch) {
    setDraftChange((prev) => (prev ? { ...prev, ...patch } : prev));
  }

  // Immediate single-permission save -- calls the EXISTING batch PUT
  // endpoint with a one-item `changes` array (no duplicate/new API), shows
  // Saving.../Saved on that row, and on failure leaves the row's prior
  // (already-effective) state untouched and surfaces a clear inline error.
  // state: "INHERIT" reuses the endpoint's existing soft-deactivate-the-
  // override semantics server-side -- this never stores Inherit as an
  // Allow or Deny.
  async function commitChange(p, { state, dataScope, reason, validFrom, validUntil }) {
    if (!selectedUserId) return;
    setRowError((prev) => { const next = new Map(prev); next.delete(p.id); return next; });
    setRowSaving((prev) => new Set(prev).add(p.id));
    const changes = [{
      permissionId: p.id,
      state,
      dataScope: state === "ALLOW" ? dataScope : undefined,
      reason: reason || undefined,
      validFrom: validFrom || undefined,
      validUntil: validUntil || undefined,
    }];
    try {
      await api.put(`/admin/access-management/users/${selectedUserId}/permission-overrides`, { changes });
      await refreshMatrix(selectedUserId);
      setOpenPopoverKey(null);
      setDraftChange(null);
      setRowJustSaved((prev) => new Set(prev).add(p.id));
      setTimeout(() => setRowJustSaved((prev) => { const next = new Set(prev); next.delete(p.id); return next; }), 2000);
    } catch (err) {
      const message = err?.response?.data?.message || "Failed to save this override change.";
      setRowError((prev) => new Map(prev).set(p.id, message));
      toast.error(message);
    } finally {
      setRowSaving((prev) => { const next = new Set(prev); next.delete(p.id); return next; });
    }
  }

  // Radio-style single-select handler for one permission row. Inherit and
  // a plain Deny (outside the one high-risk module) commit straight away;
  // Allow always needs a data scope, and Deny on the high-risk module
  // always needs a reason, so those two cases open the row's popover for
  // that one extra piece of information before saving.
  function handleStateChange(m, p, nextState) {
    if (!canManage) return;
    const current = currentState(p);
    if (nextState === current.state) return;
    const key = `${m.id}::${p.id}`;
    const isHighRisk = HIGH_RISK_MODULE_KEYS.has(m.moduleKey);

    if (nextState === "INHERIT") {
      commitChange(p, { state: "INHERIT", dataScope: null, reason: "", validFrom: "", validUntil: "" });
      return;
    }
    if (nextState === "DENY" && !isHighRisk) {
      commitChange(p, { state: "DENY", dataScope: null, reason: current.reason, validFrom: current.validFrom, validUntil: current.validUntil });
      return;
    }
    // ALLOW (always), or DENY on a high-risk module (reason required) --
    // open the popover pre-filled with a sensible default instead of
    // saving immediately.
    setOpenPopoverKey(key);
    setDraftChange({
      permissionId: p.id,
      state: nextState,
      dataScope: nextState === "ALLOW" ? (current.dataScope || p.roleDataScope || "SELF") : null,
      reason: current.reason || "",
      validFrom: current.validFrom || "",
      validUntil: current.validUntil || "",
    });
  }

  function closePopover() {
    setOpenPopoverKey(null);
    setDraftChange(null);
  }

  // ---- Module-level bulk actions --------------------------------------
  function setModuleAllToInherit(m) {
    if (!canManage) return;
    const changes = m.permissions.filter((p) => p.override).map((p) => ({ permissionId: p.id, state: "INHERIT" }));
    if (changes.length === 0) return;
    runBulkSave(m, changes, `Reset ${changes.length} override(s) to Inherit in ${m.moduleName}.`);
  }

  function requestModuleBulk(m, type) {
    if (!canManage) return;
    setBulkAction({
      moduleId: m.id,
      moduleName: m.moduleName,
      type,
      scope: "SELF",
      reason: "",
      permissions: m.permissions,
    });
  }

  async function runBulkSave(m, changes, successMessage) {
    setBulkSaving(true);
    try {
      await api.put(`/admin/access-management/users/${selectedUserId}/permission-overrides`, { changes });
      await refreshMatrix(selectedUserId);
      toast.success(successMessage || `Updated ${changes.length} permission(s) in ${m.moduleName}.`);
      setBulkAction(null);
    } catch (err) {
      toast.error(err?.response?.data?.message || "Failed to apply the bulk change.");
    } finally {
      setBulkSaving(false);
    }
  }

  function confirmBulkAction() {
    if (!bulkAction) return;
    const isHighRisk = HIGH_RISK_MODULE_KEYS.has(userDetail.modules.find((m) => m.id === bulkAction.moduleId)?.moduleKey);
    const changes = bulkAction.permissions.map((p) => ({
      permissionId: p.id,
      state: bulkAction.type === "ALLOW_ALL" ? "ALLOW" : "DENY",
      dataScope: bulkAction.type === "ALLOW_ALL" ? bulkAction.scope : undefined,
      reason: bulkAction.reason || undefined,
    }));
    if (isHighRisk && !bulkAction.reason.trim()) {
      toast.error("A reason is required for this high-risk module.");
      return;
    }
    runBulkSave(
      { id: bulkAction.moduleId, moduleName: bulkAction.moduleName },
      changes,
      `${bulkAction.type === "ALLOW_ALL" ? "Allowed" : "Denied"} all ${changes.length} permission(s) in ${bulkAction.moduleName}.`
    );
  }

  // Matrix search -- matches either the module's own name/key or any
  // permission name inside it, so searching a permission (e.g. "export")
  // surfaces its parent module even when the module name itself doesn't
  // match. moduleFilterId (the page-level module dropdown) narrows further
  // to one exact module.
  const [moduleSearch, setModuleSearch] = useState("");
  const filteredModules = useMemo(() => {
    if (!userDetail) return [];
    const q = moduleSearch.trim().toLowerCase();
    return userDetail.modules.filter((m) => {
      if (moduleFilterId && String(m.id) !== String(moduleFilterId)) return false;
      if (!q) return true;
      if (m.moduleName.toLowerCase().includes(q) || m.moduleKey.toLowerCase().includes(q)) return true;
      return m.permissions.some((p) => p.name.toLowerCase().includes(q));
    });
  }, [userDetail, moduleSearch, moduleFilterId]);

  // A module search that only matches on a permission name (not the module
  // name itself) is useless if the module stays collapsed -- auto-expand
  // those so the matching row is actually visible.
  useEffect(() => {
    const q = moduleSearch.trim().toLowerCase();
    if (!q || !userDetail) return;
    setExpanded((prev) => {
      const next = new Set(prev);
      let changed = false;
      userDetail.modules.forEach((m) => {
        const matchesPermissionOnly = !m.moduleName.toLowerCase().includes(q) && !m.moduleKey.toLowerCase().includes(q)
          && m.permissions.some((p) => p.name.toLowerCase().includes(q));
        if (matchesPermissionOnly && !next.has(m.id)) { next.add(m.id); changed = true; }
      });
      return changed ? next : prev;
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [moduleSearch, userDetail]);

  // Whether a single permission row matches the page-level status filter --
  // applied inside each expanded module's table, never changes what was
  // fetched or how effective access is computed.
  function rowMatchesStatusFilter(p) {
    if (!statusFilter) return true;
    if (statusFilter === "ALLOWED") return !!p.effectiveAllowed;
    if (statusFilter === "DENIED") return !p.effectiveAllowed;
    if (statusFilter === "OVERRIDES") return !!p.override;
    if (statusFilter === "INHERITED") return !p.override;
    return true;
  }

  // Read-only display aggregate over the already-fetched permission matrix
  // -- purely a presentational summary, never a second source of truth: it
  // does not change what is fetched, saved, or how effective access is
  // computed. Unambiguous labels: "Effective Allowed/Denied" (the resolved
  // access after role + override), "User Overrides" (permissions that have
  // a user-specific override at all, regardless of which way it resolves).
  const accessSummary = useMemo(() => {
    if (!userDetail) return null;
    let totalPermissions = 0, effectiveAllowed = 0, effectiveDenied = 0, userOverrides = 0;
    userDetail.modules.forEach((m) => m.permissions.forEach((p) => {
      totalPermissions += 1;
      if (p.effectiveAllowed) effectiveAllowed += 1; else effectiveDenied += 1;
      if (p.override) userOverrides += 1;
    }));
    return { totalPermissions, effectiveAllowed, effectiveDenied, userOverrides };
  }, [userDetail]);

  const selectedUser = userDetail?.user;
  const roleColor = colorForRole(selectedUser?.roleName);

  return (
    <div className="unit-page aup-page access-users-theme">
      <div className="unit-header aup-heading-box">
        <div className="aup-heading-content">
          <div className="aup-heading-icon">
            <i className="fa-solid fa-user-lock" aria-hidden="true"></i>
          </div>
          <div>
            <div className="unit-page-label">Access Management</div>
            <h2>User Permission Overrides</h2>
            <p>Select a user, expand a module, then choose Inherit, Allow or Deny for each permission. {canManage ? "Changes save immediately." : "You have view-only access to this screen."}</p>
          </div>
        </div>
      </div>

      <div className="aup-layout">
        {/* ---------- User selector: searchable dropdown (replaces the old
            always-visible, always-scrollable Users list) ---------- */}
        <div className="unit-card pm-selector-card">
          <div className="pm-selector-card-head">
            <h5>User Permissions</h5>
            <p>Select a user to review or override permissions</p>
          </div>
          <PermissionUserDropdown
            users={users}
            loading={loadingUsers}
            error={userListError}
            onRetry={() => fetchUsers()}
            selectedUserId={selectedUserId}
            selectedUser={selectedUser}
            onSelect={selectUser}
            filters={filters}
            onSearchChange={(value) => updateFilter("search", value)}
            onSearchSubmit={() => fetchUsers()}
            onQuickFilterChange={applyFilterChange}
            onClearFilters={clearFilters}
            roleOptions={roleOptions}
            departmentOptions={departmentOptions}
            designationOptions={designationOptions}
            orgColumnsAvailable={orgColumnsAvailable}
          />
        </div>

        {/* ---------- User Details + permission matrix ---------- */}
        <div className="unit-card aup-details-section">
          {!selectedUserId && (
            <div className="aup-empty-state">
              <div className="aup-empty-icon"><FaUserCircle /></div>
              <h5>Select a user</h5>
              <p>Choose a user from the dropdown above to view role permissions, user overrides and effective access.</p>
            </div>
          )}

          {selectedUserId && loadingMatrix && <div className="am-empty"><Spinner animation="border" size="sm" /> Loading permissions…</div>}
          {selectedUserId && !loadingMatrix && matrixError && (
            <div className="am-empty am-error">
              {matrixError}
              <Button className="unit-btn unit-btn-light" size="sm" onClick={() => loadMatrix(selectedUserId)}>
                <i className="fa-solid fa-rotate-right" aria-hidden="true"></i> Try Again
              </Button>
            </div>
          )}

          {selectedUserId && !loadingMatrix && !matrixError && userDetail && (
            <>
              <PermissionSummaryHeader user={selectedUser} roleColor={roleColor} accessSummary={accessSummary} />

              <PermissionLegend />

              {userDetail.isLegacyInactiveBypass && (
                <div className="am-unsaved-banner am-legacy-banner">
                  <i className="fa-solid fa-circle-info" aria-hidden="true"></i>
                  This user is not marked active, but inactive-user denial is not yet enforced platform-wide (documented temporary bypass).
                </div>
              )}

              {!canManage && (
                <div className="am-unsaved-banner am-readonly-banner">
                  <FaLock aria-hidden="true" />
                  You have view-only access to permission overrides. Contact an administrator with manage access to make changes.
                </div>
              )}

              <PermissionFilterBar
                search={moduleSearch}
                onSearchChange={setModuleSearch}
                statusFilter={statusFilter}
                onStatusFilterChange={setStatusFilter}
                moduleFilterId={moduleFilterId}
                onModuleFilterChange={setModuleFilterId}
                modules={userDetail.modules}
              />

              <div className="am-matrix-wrapper">
                {filteredModules.length === 0 && <div className="am-empty">No modules match your search.</div>}
                {filteredModules.map((m) => (
                  <PermissionModuleCard
                    key={m.id}
                    m={m}
                    isExpanded={expanded.has(m.id)}
                    onToggleExpand={() => toggleExpand(m.id)}
                    canManage={canManage}
                    bulkSaving={bulkSaving}
                    onSetAllInherit={() => setModuleAllToInherit(m)}
                    onRequestBulk={(type) => requestModuleBulk(m, type)}
                    visibleRows={m.permissions.filter(rowMatchesStatusFilter)}
                    openPopoverKey={openPopoverKey}
                    draftChange={draftChange}
                    updateDraft={updateDraft}
                    closePopover={closePopover}
                    commitChange={commitChange}
                    rowSaving={rowSaving}
                    rowJustSaved={rowJustSaved}
                    rowError={rowError}
                    onStateChange={handleStateChange}
                  />
                ))}
              </div>
            </>
          )}
        </div>
      </div>

      <Modal show={!!bulkAction} onHide={() => (bulkSaving ? null : setBulkAction(null))} centered dialogClassName="admin-teal-modal">
        <Modal.Header closeButton={!bulkSaving}>
          <Modal.Title>{bulkAction?.type === "ALLOW_ALL" ? "Allow all permissions" : "Deny all permissions"}</Modal.Title>
        </Modal.Header>
        {bulkAction && (
          <>
            <Modal.Body>
              <p>
                This will set all <strong>{bulkAction.permissions.length}</strong> permission(s) in{" "}
                <strong>{bulkAction.moduleName}</strong> to <strong>{bulkAction.type === "ALLOW_ALL" ? "Allow" : "Deny"}</strong> for
                this user only. The <strong>{bulkAction.moduleName}</strong> role permissions are not affected.
              </p>
              {bulkAction.type === "ALLOW_ALL" && (
                <div className="am-pop-row">
                  <span>Override Scope (applied to every permission)</span>
                  <Form.Select
                    size="sm"
                    value={bulkAction.scope}
                    onChange={(e) => setBulkAction((prev) => ({ ...prev, scope: e.target.value }))}
                  >
                    {SUPPORTED_SCOPES.map((s) => <option key={s} value={s}>{scopeLabel(s)}</option>)}
                  </Form.Select>
                </div>
              )}
              <div className="am-pop-row">
                <span>Reason{HIGH_RISK_MODULE_KEYS.has(userDetail?.modules.find((m) => m.id === bulkAction.moduleId)?.moduleKey) ? " (required)" : " (optional)"}</span>
                <Form.Control
                  size="sm"
                  value={bulkAction.reason}
                  onChange={(e) => setBulkAction((prev) => ({ ...prev, reason: e.target.value }))}
                  placeholder="Reason for this bulk change"
                />
              </div>
            </Modal.Body>
            <Modal.Footer>
              <Button className="unit-btn unit-btn-light" onClick={() => setBulkAction(null)} disabled={bulkSaving}>
                <i className="fa-solid fa-xmark" aria-hidden="true"></i> Cancel
              </Button>
              <Button className="unit-btn unit-btn-primary" onClick={confirmBulkAction} disabled={bulkSaving}>
                {bulkSaving ? "Saving…" : <><i className="fa-solid fa-check" aria-hidden="true"></i> Confirm</>}
              </Button>
            </Modal.Footer>
          </>
        )}
      </Modal>


      <style>{`
        .aup-page { padding-bottom: 24px; }
        .am-unsaved-banner { background: var(--admin-warning-soft, var(--warning-bg, #fffbeb)); color: var(--admin-warning, var(--warning, #d97706)); border-bottom: 1px solid var(--admin-warning, var(--warning, #d97706)); padding: 10px 22px; font-size: 13px; font-weight: 700; display: flex; align-items: center; gap: 10px; flex-wrap: wrap; }
        .am-legacy-banner { background: #eff6ff; color: #1d4ed8; border-color: #bfdbfe; }
        .am-empty { padding: 40px 20px; text-align: center; color: #667085; font-size: 14px; display: flex; flex-direction: column; align-items: center; gap: 10px; }
        .am-error { color: #b42318; }
        .am-muted { color: #94a3b8; font-size: 11px; }
        .am-row-selected td { background: #eff6ff !important; }
        .am-user-summary { display: grid; grid-template-columns: repeat(auto-fit, minmax(150px, 1fr)); gap: 14px; padding: 16px 22px; border-bottom: 1px solid #eef2f6; }
        .am-user-summary > div { display: flex; flex-direction: column; min-width: 0; }
        .am-user-summary span { display: flex; align-items: center; gap: 6px; font-size: 10px; font-weight: 700; color: #667085; text-transform: uppercase; letter-spacing: 0.04em; }
        .am-user-summary span i { font-size: 10px; color: #94a3b8; }
        .am-user-summary strong { font-size: 13.5px; color: #241f1a; font-weight: 600; margin-top: 2px; }
        .am-matrix-wrapper { padding: 12px 22px; }
        .am-user-module-group { border: 1px solid #e6eaf0; border-radius: 12px; margin-bottom: 10px; overflow: hidden; }
        .am-module-header-row { display: flex; align-items: center; justify-content: space-between; gap: 10px; background: #f8fafc; flex-wrap: wrap; }
        .am-module-header { flex: 1; min-width: 220px; text-align: left; background: transparent; border: none; padding: 10px 14px; font-weight: 700; color: #241f1a; display: flex; align-items: center; gap: 8px; cursor: pointer; }
        .am-module-header-title { white-space: nowrap; }
        .am-module-header-meta { font-size: 12px; font-weight: 600; color: #667085; text-transform: none; letter-spacing: normal; display: flex; align-items: center; gap: 6px; flex-wrap: wrap; }
        .am-high-risk-tag { background: #fef3f2; color: #b42318; border-radius: 999px; padding: 1px 8px; font-size: 10.5px; font-weight: 700; text-transform: uppercase; letter-spacing: 0.03em; }
        .am-module-bulk-actions { display: flex; gap: 6px; flex-wrap: wrap; padding: 8px 14px; }
        .am-bulk-allow-btn { color: #027a48 !important; border-color: rgba(2, 122, 72, 0.35) !important; }
        .am-bulk-deny-btn { color: #b42318 !important; border-color: rgba(180, 35, 24, 0.35) !important; }
        .am-readonly-banner { background: #f1f5f9; color: #475467; border-color: #cbd5e1; }

        /* ---------- Page-level permission filters (search + status +
           module) shown above the module list. ---------- */
        .aup-matrix-filter-select { max-width: 220px; height: 38px; font-size: 13px; border: 1px solid #d9e1ec; border-radius: 8px; }

        /* ---------- Permission matrix table: Permission | Role Access |
           User Override (Inherit/Allow/Deny radio group) | Effective
           Access -- replaces the old drag-and-drop kanban board. Radios are
           single-select per row, same INHERIT/ALLOW/DENY enum as before. */
        .am-matrix-table-wrapper { overflow-x: auto; }
        .am-matrix-table { width: 100%; border-collapse: collapse; font-size: 13px; }
        .am-matrix-table thead th { text-align: left; padding: 8px 14px; font-size: 11px; font-weight: 700; text-transform: uppercase; letter-spacing: 0.04em; color: #667085; background: #f8fafc; border-bottom: 1px solid #e6eaf0; white-space: nowrap; }
        .am-matrix-table tbody td { padding: 10px 14px; border-bottom: 1px solid #f1f5f9; vertical-align: top; }
        .am-matrix-table tbody tr:last-child td { border-bottom: none; }
        .am-matrix-table tr.am-row-overridden > td.am-mt-perm { border-left: 3px solid var(--admin-warning, #d97706); }
        .am-mt-perm-name { font-weight: 600; color: #241f1a; }
        .am-cell-scope-badge { font-size: 9.5px; font-weight: 700; color: #6d1530; margin-top: 4px; text-transform: uppercase; letter-spacing: 0.03em; }
        .am-row-feedback { margin-top: 4px; font-size: 11.5px; min-height: 15px; }
        .am-row-saving { color: #667085; display: inline-flex; align-items: center; gap: 5px; }
        .am-row-saved { color: #027a48; font-weight: 700; display: inline-flex; align-items: center; gap: 4px; }
        .am-row-error-text { color: #b42318; font-weight: 600; }

        .am-row-editor-tr td { background: #f8fafc; }
        .am-row-editor { display: flex; flex-direction: column; gap: 8px; padding: 6px 4px; max-width: 480px; }
        .am-row-editor-title { font-size: 12.5px; font-weight: 700; color: #241f1a; }
        .am-pop-required-hint { color: #b42318; font-size: 12px; font-weight: 600; }
        .am-pop-actions { display: flex; justify-content: flex-end; gap: 8px; margin-top: 4px; }

        /* ---------- Mobile / tablet: stacked-card layout per permission
           instead of a horizontally-scrolled table -- Permission name /
           Role / Override radios / Effective, each on its own line. ---------- */
        @media (max-width: 768px) {
          .am-matrix-table thead { display: none; }
          .am-matrix-table, .am-matrix-table tbody, .am-matrix-table tr, .am-matrix-table td { display: block; width: 100%; }
          .am-matrix-table tr { border: 1px solid #e6eaf0; border-radius: 10px; margin-bottom: 10px; padding: 8px 0; }
          .am-matrix-table tr.am-row-editor-tr { border: none; margin: -10px 0 10px; }
          .am-matrix-table td { border-bottom: none; padding: 6px 14px; }
          .am-matrix-table td[data-label]::before { content: attr(data-label); display: block; font-size: 10.5px; font-weight: 700; text-transform: uppercase; letter-spacing: 0.03em; color: #94a3b8; margin-bottom: 4px; }
          .pm-matrix-table td.am-mt-perm, .pm-matrix-table td.pm-compare, .pm-matrix-table td.pm-control { width: 100%; }
          .pm-filter-bar { flex-direction: column; align-items: stretch; position: static; }
          .aup-matrix-filter-select { max-width: none; }
        }

        /* ---------- Vertical layout: selector card on top, User Details
           below, both always full width. ---------- */
        .aup-layout { display: flex; flex-direction: column; gap: 20px; padding: 18px 34px; }
        .aup-details-section { width: 100%; min-width: 0; box-sizing: border-box; }

        /* .aup-filter-search / .aup-search-icon are also used by
           PermissionFilterBar.jsx's own module search box, so their base
           rules stay here rather than moving into permissions.css. */
        .aup-filter-search { display: flex; align-items: center; gap: 8px; border: 1px solid #d9e1ec; border-radius: 10px; padding: 0 12px; margin: 0 18px 12px; height: 40px; }
        .aup-filter-search input { border: none; box-shadow: none !important; padding: 0; height: 38px; }
        .aup-search-icon { color: #64748b; }

        /* ---------- User Details hero ---------- */
        .aup-hero { display: flex; align-items: center; gap: 16px; padding: 20px 22px; border-bottom: 1px solid #eef2f6; }
        .aup-hero-avatar { width: 54px; height: 54px; min-width: 54px; border-radius: 14px; display: flex; align-items: center; justify-content: center; font-size: 19px; font-weight: 700; }
        .aup-hero-text { min-width: 0; flex: 1; }
        .aup-hero-role-badge { display: inline-block; font-size: 11px; font-weight: 700; text-transform: capitalize; padding: 3px 10px; border-radius: 999px; margin-bottom: 4px; }
        .aup-hero-text h3 { font-size: 19px; font-weight: 700; color: #241f1a; margin: 0; }
        .aup-hero-meta { display: flex; flex-wrap: wrap; gap: 4px 10px; margin: 4px 0 0; }
        .aup-hero-meta span { font-size: 12.5px; color: #667085; }
        .aup-hero-meta span:not(:last-child)::after { content: "·"; margin-left: 10px; color: #cbd5e1; }

        .aup-access-summary { display: flex; flex-wrap: wrap; gap: 10px; padding: 16px 22px; border-bottom: 1px solid #eef2f6; }
        .aup-summary-chip { display: inline-flex; align-items: center; gap: 7px; background: #faf7f1; border: 1px solid #e7ded0; color: #6b6259; border-radius: 999px; padding: 6px 14px; font-size: 12.5px; font-weight: 700; }
        .aup-summary-chip i { font-size: 11px; }
        .aup-summary-chip-success { background: #e3f7ef; border-color: rgba(21,154,112,0.25); color: #159a70; }
        .aup-summary-chip-danger { background: #fceaec; border-color: rgba(220,76,90,0.25); color: #dc4c5a; }

        /* ---------- Permission matrix: flat "Module x action" checkbox
           grid, closely following AccessManagementRolesPage.jsx's own
           am-matrix convention for a consistent look between the two
           Access Management pages. ---------- */
        .aup-matrix-search { padding: 16px 22px 0; }
        .aup-matrix-search .unit-search { max-width: 320px; }
        .am-matrix-wrapper { padding: 4px 0 12px; overflow: auto; max-height: 65vh; }
        table.aup-matrix { min-width: 640px; margin: 0 !important; }
        table.aup-matrix thead th { position: sticky; top: 0; z-index: 2; background: var(--admin-primary, var(--primary, #6d1530)); color: #fff; white-space: nowrap; }
        .am-sticky-col { position: sticky; left: 0; background: #ffffff !important; z-index: 1; min-width: 170px; text-align: left !important; font-weight: 700; color: #241f1a; }
        thead .am-sticky-col { background: var(--admin-primary, var(--primary, #6d1530)) !important; z-index: 3; color: #fff; }
        .am-disabled-cell { color: #cbd5e1; text-align: center; }
        .am-perm-cell { min-width: 78px; text-align: center; }
        .am-cell-inner { display: flex; align-items: center; justify-content: center; gap: 6px; }
        .am-cell-inner .form-check-input { width: 18px; height: 18px; cursor: pointer; }
        .am-cell-more { border: none; background: none; padding: 2px 4px; color: #94a3b8; cursor: pointer; border-radius: 6px; line-height: 1; }
        .am-cell-more:hover { background: #f1f5f9; color: #6d1530; }
        .am-changed-cell { background: var(--admin-warning-soft, var(--warning-bg, #fffbeb)) !important; }
        .am-cell-scope-badge { font-size: 9.5px; font-weight: 700; color: #6d1530; margin-top: 2px; text-transform: uppercase; letter-spacing: 0.03em; }

        .am-cell-popover { max-width: 280px; }
        .am-pop-row { display: flex; flex-direction: column; gap: 4px; margin-bottom: 10px; }
        .am-pop-row > span:first-child { font-size: 10.5px; font-weight: 700; color: #667085; text-transform: uppercase; letter-spacing: 0.03em; }
        .am-pop-row-split { flex-direction: row; gap: 10px; }
        .am-pop-row-split > div { flex: 1; display: flex; flex-direction: column; gap: 4px; }
        .am-pop-row-split > div > span { font-size: 10px; font-weight: 700; color: #667085; text-transform: uppercase; }
        .am-pop-effective { flex-direction: row; align-items: center; justify-content: space-between; margin-bottom: 4px; }
        .am-pop-reset { padding: 0; font-size: 12.5px; }
        .am-pop-section-label { font-size: 10px; font-weight: 800; color: #94a3b8; text-transform: uppercase; letter-spacing: 0.05em; margin: 12px 0 6px; border-top: 1px solid #eef2f6; padding-top: 10px; }
        .am-pop-section-label:first-child { margin-top: 0; border-top: none; padding-top: 0; }
        .am-pop-scope-value { font-size: 13px; font-weight: 600; color: #241f1a; }

        .aup-empty-state { display: flex; flex-direction: column; align-items: center; justify-content: center; text-align: center; min-height: 200px; padding: 26px 30px; color: #667085; }
        .aup-empty-icon { font-size: 32px; color: #cbd5e1; margin-bottom: 10px; }
        .aup-empty-state h5 { font-size: 18px; font-weight: 700; color: #241f1a; margin: 0 0 8px; }
        .aup-empty-state p { font-size: 14px; max-width: 380px; margin: 0; }

        @media (max-width: 992px) {
          .aup-layout { padding: 14px 18px; gap: 14px; }
        }

        @media (max-width: 480px) {
          .aup-layout { padding: 12px 14px; gap: 12px; }
          .aup-hero { flex-wrap: wrap; }
        }
      `}</style>
    </div>
  );
}
